import { useEffect, useState } from 'react';
import type { Movie } from '@/mocks/movies';

interface StillsGalleryProps {
  movie: Movie;
}

export default function StillsGallery({ movie }: StillsGalleryProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    if (activeIndex === null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveIndex(null);
      if (event.key === 'ArrowRight') setActiveIndex((prev) => ((prev ?? 0) + 1) % movie.stills.length);
      if (event.key === 'ArrowLeft')
        setActiveIndex((prev) => ((prev ?? 0) - 1 + movie.stills.length) % movie.stills.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIndex, movie.stills.length]);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        {movie.stills.map((still, index) => (
          <button
            key={still}
            type="button"
            onClick={() => setActiveIndex(index)}
            className="group relative w-full aspect-video rounded-lg overflow-hidden bg-background-800 border border-background-800 hover:border-primary-500/60 transition-colors cursor-pointer"
          >
            <img
              src={still}
              alt={`${movie.title} 剧照 ${index + 1}`}
              title={`${movie.title} 精彩剧照`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-background-950/0 group-hover:bg-background-950/30 transition-colors flex items-center justify-center">
              <span className="w-10 h-10 rounded-full bg-background-950/70 flex items-center justify-center text-foreground-100 opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-zoom-in-line text-lg"></i>
              </span>
            </span>
          </button>
        ))}
      </div>

      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-[90] bg-background-950/92 backdrop-blur-sm flex items-center justify-center px-4 animate-fade-in"
          onClick={() => setActiveIndex(null)}
        >
          <button
            type="button"
            onClick={() => setActiveIndex(null)}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-background-800 border border-background-700 flex items-center justify-center text-foreground-100 hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer"
            aria-label="关闭"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setActiveIndex((prev) => ((prev ?? 0) - 1 + movie.stills.length) % movie.stills.length);
            }}
            className="absolute left-4 md:left-10 w-11 h-11 rounded-full bg-background-800/80 border border-background-700 flex items-center justify-center text-foreground-100 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <div className="max-w-4xl w-full" onClick={(event) => event.stopPropagation()}>
            <img
              src={movie.stills[activeIndex]}
              alt={`${movie.title} 剧照 ${activeIndex + 1}`}
              title={`${movie.title} 精彩剧照`}
              className="w-full rounded-lg border border-background-700"
            />
            <p className="mt-3 text-center text-sm text-foreground-300">
              {movie.title} · 剧照 {activeIndex + 1} / {movie.stills.length}
            </p>
          </div>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setActiveIndex((prev) => ((prev ?? 0) + 1) % movie.stills.length);
            }}
            className="absolute right-4 md:right-10 w-11 h-11 rounded-full bg-background-800/80 border border-background-700 flex items-center justify-center text-foreground-100 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>
      )}
    </div>
  );
}